import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// Magnetic button/link that gently follows the cursor on desktop.
export const MagneticButton = ({ children, to, href, onClick, className = "", variant = "primary", ...rest }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });

  const handleMove = (e) => {
    if (reduce) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - (rect.left + rect.width / 2)) * 0.25);
    y.set((e.clientY - (rect.top + rect.height / 2)) * 0.25);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  const cls = cn(variant === "primary" ? "btn-primary" : "btn-ghost", className);
  const inner = <span className="pointer-events-none">{children}</span>;

  const common = {
    ref,
    onMouseMove: handleMove,
    onMouseLeave: reset,
    style: { x: sx, y: sy },
    className: cls,
    ...rest,
  };

  if (to) {
    return (
      <motion.div {...common} style={{ x: sx, y: sy, display: "inline-flex" }} className="">
        <Link to={to} onClick={onClick} className={cls}>
          {inner}
        </Link>
      </motion.div>
    );
  }
  if (href) {
    return (
      <motion.a {...common} href={href} onClick={onClick}>
        {inner}
      </motion.a>
    );
  }
  return (
    <motion.button {...common} type="button" onClick={onClick}>
      {inner}
    </motion.button>
  );
};

// Desktop-only soft cursor spotlight (restrained ambient glow).
export const CursorSpotlight = () => {
  const reduce = useReducedMotion();
  const [pos, setPos] = useState({ x: -600, y: -600 });
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (reduce) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    setEnabled(true);
    const onMove = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [reduce]);

  if (reduce || !enabled) return null;
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[52] hidden lg:block">
      <div
        className="absolute h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          left: pos.x,
          top: pos.y,
          background: "radial-gradient(circle, rgba(0,240,255,0.08), transparent 62%)",
          transition: "left 0.14s ease-out, top 0.14s ease-out",
        }}
      />
    </div>
  );
};
